export type PhotographerSpecialization = string;

// Same hardcoded fallback table + substring-matching approach as
// Admin/src/components/Services.tsx's DEFAULT_PAYOUT_TABLE (and Backend's/
// Photographer app's copies) — duplicated here (same app, different file)
// purely to answer "does this service actually show a payout figure on the
// Services page" without importing UI code into a constants module.
const DEFAULT_PAYOUT_TABLE_MATCHES: string[][] = [
    ['photoshoot', 'personalphotographer', 'traditionalphotographer'],
    ['candid', 'candidphotographer'],
    ['reelsiphone', 'iphonereels', 'iphonereel', 'reeliphone'],
    ['reelsdslr', 'dslrreels', 'dslrreel', 'reeldslr', 'cinematographerforreels', 'cinematographer'],
];

function normaliseToken(input: any): string {
    return String(input || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function hasKnownPayout(service: any): boolean {
    if (Array.isArray(service?.payout_hour_tiers) && service.payout_hour_tiers.length > 0) return true;
    if (String(service?.pricing_type || 'per_hour') === 'per_count') return true; // its own price_per_hour IS the payout
    const nameKey = normaliseToken(service?.key);
    const specKey = normaliseToken(service?.specialization_name);
    return DEFAULT_PAYOUT_TABLE_MATCHES.some((matches) => matches.some((m) => nameKey.includes(m) || specKey.includes(m)));
}

/**
 * Build checkbox labels: every active MiscData.services entry that actually
 * has a real payout figure on the Services page (admin-set payout tiers, a
 * default-table match, or a per-count service whose price IS the payout) —
 * plus any strings already on the photographer's own record, so an existing
 * (possibly legacy) assignment never silently disappears from view.
 *
 * Previously this also unconditionally injected 4 hardcoded "core" names
 * (Personal Photographer / Candid Photographer / Cinematographer / Edited
 * Reels Count) — which didn't even match the real service names configured
 * in Services (Photoshoot / Candid Photographer / Reels DSLR / Editing
 * Reels), and let no-payout test entries (e.g. "Test Service") leak in
 * unfiltered. `miscInput` may be a single Misc document `{ services: [...] }`
 * or an array of documents (merged on server is preferred).
 */
export function mergeSpecializationOptionsFromMisc(
    miscInput: any,
    extraLabelArrays?: (readonly string[] | string[] | undefined | null)[],
): string[] {
    const set = new Set<string>();
    const services = collectServicesFromMisc(miscInput);
    for (const s of services) {
        if (s && s.is_active === false) continue;
        if (!hasKnownPayout(s)) continue;
        const lab = String(s?.specialization_name || '').trim();
        if (lab) set.add(lab);
    }
    if (extraLabelArrays) {
        for (const arr of extraLabelArrays) {
            if (!Array.isArray(arr)) continue;
            for (const x of arr) {
                const t = String(x || '').trim();
                if (t) set.add(t);
            }
        }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
}

function collectServicesFromMisc(miscInput: any): any[] {
    if (Array.isArray(miscInput)) {
        const out: any[] = [];
        const seen = new Set<string>();
        for (const doc of miscInput) {
            for (const s of Array.isArray(doc?.services) ? doc.services : []) {
                const name = String(s?.specialization_name || '').trim();
                const key = String(s?.key || '').trim() || name;
                const dedupe = `${key}::${name}`;
                if (!name && !key) continue;
                if (!seen.has(dedupe)) {
                    seen.add(dedupe);
                    out.push(s);
                }
            }
        }
        return out;
    }
    return Array.isArray(miscInput?.services) ? miscInput.services : [];
}
